'use client';

import { c as _c } from "react/compiler-runtime";
import { formatFundingPreset, withdrawalArtwork } from '@morfi/onramp-sdk';
import type { useFundingCurrency, useWithdrawalAmount, useWithdrawalCheckout } from '@morfi/onramp-sdk/react';
import { useLocale, useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AnimatedButton } from '@/components/ui/animated-button';
import { useFaucetClaim } from '@/hooks/faucet/use-faucet-claim';
import { getActiveNetwork } from '@/lib/network/active';
import { FundingAmount } from '../checkout/amount/funding-amount';
type Flow = ReturnType<typeof useWithdrawalCheckout>;
export function WithdrawAmountScreen(t0) {
  const $ = _c(54);
  const {
    flow,
    amount,
    currency,
    context,
    value,
    setValue,
    setInputUnits,
    expired,
    retryRequest,
    run,
    fees
  } = t0;
  const t = useTranslations("onramp");
  const locale = useLocale();
  const faucetText = useTranslations("portfolio.faucet_banner");
  const faucet = useFaucetClaim("portfolio_banner");
  let t1;
  if ($[0] !== flow.method || $[1] !== t) {
    t1 = flow.method ? t(flow.method) : t("withdraw_title");
    $[0] = flow.method;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== flow.method) {
    t2 = flow.method ? <OptimizedImage nsfw={false} variant="thumbnail" preview={false} loadReveal="none" rounded="" imageClassName="object-contain" src={`/assets/onramp/method-${withdrawalArtwork[flow.method]}.png`} alt="" size="size-5" className="bg-transparent" /> : undefined;
    $[3] = flow.method;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== amount.maximumUnits || $[6] !== currency.currency || $[7] !== currency.decimals || $[8] !== locale || $[9] !== t) {
    t3 = units => units === amount.maximumUnits ? t("withdraw_max") : formatFundingPreset(units, currency.decimals, currency.currency, locale);
    $[5] = amount.maximumUnits;
    $[6] = currency.currency;
    $[7] = currency.decimals;
    $[8] = locale;
    $[9] = t;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== amount.amountD6 || $[12] !== amount.boundary || $[13] !== amount.loading || $[14] !== amount.quote || $[15] !== currency.status || $[16] !== expired || $[17] !== flow.commands.isError || $[18] !== flow.commands.isPending || $[19] !== flow.context || $[20] !== retryRequest || $[21] !== run || $[22] !== t) {
    t4 = expired || flow.context.isError || amount.quote.isError || flow.commands.isError ? <AnimatedButton className="h-11 w-full" loading={amount.loading || flow.commands.isPending} disabled={flow.commands.isPending || amount.loading || !amount.amountD6 || Boolean(amount.boundary) || currency.status !== "ready"} onClick={() => {
      if (expired || flow.context.isError) {
        retryRequest();
      } else {
        if (amount.quote.isError) {
          amount.quote.refetch();
        } else {
          if (amount.amountD6 && !amount.boundary) {
            run({
              kind: "quote",
              amountD6: amount.amountD6
            });
          }
        }
      }
    }}>{t(expired ? "sign_in" : "retry")}</AnimatedButton> : undefined;
    $[11] = amount.amountD6;
    $[12] = amount.boundary;
    $[13] = amount.loading;
    $[14] = amount.quote;
    $[15] = currency.status;
    $[16] = expired;
    $[17] = flow.commands.isError;
    $[18] = flow.commands.isPending;
    $[19] = flow.context;
    $[20] = retryRequest;
    $[21] = run;
    $[22] = t;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  const t5 = !flow.commands.isPending && currency.status === "ready" && (flow.method === "manual_withdraw" || Boolean(amount.estimate));
  let t6;
  if ($[24] !== context.balanceD6 || $[25] !== context.source || $[26] !== faucet || $[27] !== faucetText || $[28] !== flow.context) {
    t6 = context.balanceD6 === "0" && getActiveNetwork() !== "mainnet" ? <AnimatedButton variant="tertiary" loading={faucet.isPending} onClick={() => faucet.claim(context.source, {
      onClaimed: () => {
        flow.context.refetch();
      }
    })}>{faucetText("title")}</AnimatedButton> : null;
    $[24] = context.balanceD6;
    $[25] = context.source;
    $[26] = faucet;
    $[27] = faucetText;
    $[28] = flow.context;
    $[29] = t6;
  } else {
    t6 = $[29];
  }
  let t7;
  if ($[30] !== amount.amountD6 || $[31] !== amount.boundary || $[32] !== run) {
    t7 = () => {
      if (amount.amountD6 && !amount.boundary) {
        run({
          kind: "quote",
          amountD6: amount.amountD6
        });
      }
    };
    $[30] = amount.amountD6;
    $[31] = amount.boundary;
    $[32] = run;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  let t8;
  if ($[34] !== amount.boundary || $[35] !== amount.estimate || $[36] !== amount.loading || $[37] !== amount.presets || $[38] !== currency.currency || $[39] !== currency.decimals || $[40] !== fees || $[41] !== flow.back || $[42] !== flow.commands.isPending || $[43] !== setInputUnits || $[44] !== setValue || $[45] !== t1 || $[46] !== t2 || $[47] !== t3 || $[48] !== t4 || $[49] !== t5 || $[50] !== t6 || $[51] !== t7 || $[52] !== value) {
    t8 = <FundingAmount methodLabel={t1} methodArtwork={t2} currency={currency.currency} decimals={currency.decimals} presets={amount.presets} presetLabel={t3} estimate={amount.estimate} quoteLoading={amount.loading} primaryAction={t4} value={value} onValueChange={setValue} onAmountChange={setInputUnits} canContinue={t5} pending={flow.commands.isPending} amountBoundary={amount.boundary} boundaryFeedback="snackbar" feeDisclosure={fees} recovery={t6} onChangeMethod={flow.back} onContinue={t7} />;
    $[34] = amount.boundary;
    $[35] = amount.estimate;
    $[36] = amount.loading;
    $[37] = amount.presets;
    $[38] = currency.currency;
    $[39] = currency.decimals;
    $[40] = fees;
    $[41] = flow.back;
    $[42] = flow.commands.isPending;
    $[43] = setInputUnits;
    $[44] = setValue;
    $[45] = t1;
    $[46] = t2;
    $[47] = t3;
    $[48] = t4;
    $[49] = t5;
    $[50] = t6;
    $[51] = t7;
    $[52] = value;
    $[53] = t8;
  } else {
    t8 = $[53];
  }
  return t8;
}